// src/components/frontend/footer/Footer.tsx

// 1) Imports make this file a module immediately.
import { Box, Container, Grid, Link, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import BottomFooter from "./BotomFooter";
import SocialMedia from "./SocialMedia";
import TopFooter from "./TopFooterBar";

type NavItem = { labelKey: string; href: string };

const helpful: NavItem[] = [
  { labelKey: "footer.aboutUs", href: "/about-us" },
  { labelKey: "footer.termsOfService", href: "/terms" },
  { labelKey: "footer.privacyPolicy", href: "/privacyPolicy" },
  { labelKey: "footer.returnPolicy", href: "/return-policy" },
  { labelKey: "footer.shippingPolicy", href: "/shipping-policy" },
];

const contactInfo: NavItem[] = [
  { labelKey: "support@us2tjk.com", href: "mailto:support@us2tjk.com" },
];

function LinkColumn({ title, items }: { title: string; items: NavItem[] }) {
  const { t: myTranslate } = useTranslation();
  return (
    <Stack spacing={1.5}>
      <Typography
        variant="h6"
        fontWeight={700}
        className="notranslate"
        translate="no"
        sx={{
          color: "#F7A81B", // Using website orange color
          fontSize: "1.1rem",
        }}
      >
        {title}
      </Typography>
      <Stack spacing={1}>
        {items.map(({ labelKey, href }) => (
          <Link
            key={labelKey}
            href={href}
            underline="none"
            className="notranslate"
            translate="no"
            sx={{
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: "0.95rem",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "none",
              },
              "&:focus": {
                textDecoration: "none",
              },
              "&:visited": {
                textDecoration: "none",
              },
              "&:active": {
                textDecoration: "none",
              },
            }}
          >
            {labelKey.includes("@") ? labelKey : myTranslate(labelKey)}
          </Link>
        ))}
      </Stack>
    </Stack>
  );
}

// 2) Exporting the component keeps the file a module under isolatedModules.
export default function Footer() {
  const { t: myTranslate } = useTranslation();
  const aboutText = myTranslate("footer.aboutText");

  return (
    <Box
      component="footer"
      sx={{
        mt: 0,
        pt: 1,
        pb: {
          xs: "calc(56px + env(safe-area-inset-bottom, 0px))",
          sm: 0,
        },
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background:
            "linear-gradient(90deg, transparent 0%, rgba(37, 99, 235, 0.6) 50%, transparent 100%)",
        },
      }}
    >
      {/* Feature highlights section - Desktop only */}
      <Box sx={{ display: "none" }}>
        <TopFooter />
      </Box>

      {/* Main footer content */}
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
        <Grid container spacing={{ xs: 3, md: 4, lg: 6 }}>
          {/* Company info and logo */}
          <Grid item xs={12} md={5}>
            <Stack spacing={4} sx={{ pr: { md: 4 } }}>
              {/* Logo and description - Mobile: stacked, Desktop: stacked */}
              <Stack
                spacing={2}
                sx={{
                  display: { xs: "flex", md: "none" },
                  alignItems: "center",
                }}
              >
                {/* Logo with bag and text - visible on mobile */}
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    component="img"
                    src="/logos/logo-png.png"
                    alt="US2TJK Logo"
                    sx={{
                      height: 50,
                      width: "auto",
                      display: "block",
                    }}
                  />
                  <Box
                    component="img"
                    src="/logos/textLogoBlack.png"
                    alt="US2TJK"
                    sx={{
                      height: 35,
                      width: "auto",
                      display: "block",
                    }}
                  />
                </Stack>

                {/* Description - Mobile: below logo */}
                <Typography
                  variant="body1"
                  className="notranslate"
                  translate="no"
                  sx={{
                    lineHeight: 1.7,
                    fontSize: "0.9rem",
                    color: "rgba(255, 255, 255, 0.8)",
                    textAlign: "center",
                  }}
                >
                  {aboutText}
                </Typography>
              </Stack>

              {/* Logo and company name - Desktop only */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ display: { xs: "none", md: "flex" } }}
              >
                <Box
                  component="img"
                  src="/logos/logo-png.png"
                  alt="US2TJK"
                  sx={{
                    height: 80,
                    width: "auto",
                    display: "block",
                  }}
                />
                <Box
                  component="img"
                  src="/logos/textLogoBlack.png"
                  alt="US2TJK"
                  sx={{
                    height: 30,
                    width: "auto",
                    display: "block",
                  }}
                />
              </Stack>

              {/* About text - Desktop only */}
              <Typography
                variant="body1"
                className="notranslate"
                translate="no"
                sx={{
                  lineHeight: 1.7,
                  fontSize: "1rem",
                  color: "rgba(255, 255, 255, 0.8)",
                  display: { xs: "none", md: "block" },
                }}
              >
                {aboutText}
              </Typography>

              {/* Feature highlights section - Mobile only */}
              <Box sx={{ display: "none" }}>
                <TopFooter />
              </Box>
            </Stack>
          </Grid>

          {/* Links sections */}
          <Grid item xs={6} sm={6} md={3.5}>
            <LinkColumn title={myTranslate("footer.helpful")} items={helpful} />
          </Grid>

          <Grid item xs={6} sm={6} md={3.5}>
            <Stack spacing={3}>
              <LinkColumn
                title={myTranslate("footer.support")}
                items={contactInfo}
              />

              {/* Social media icons */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  mb={2}
                  className="notranslate"
                  translate="no"
                  sx={{ color: "#F7A81B" }} // Using website orange color
                >
                  {myTranslate("footer.followUsOn")}
                </Typography>
                <SocialMedia />
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Bottom section with subtle divider */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0f172a 0%, #020617 100%)",
          borderTop: "1px solid rgba(37, 99, 235, 0.3)",
        }}
      >
        <BottomFooter />
      </Box>
    </Box>
  );
}
